import ApiService from '@/services/apiService';
import LambdaApiService from '@/services/apiService/lambda';
import MonitoringEnableDialog from '@/components/core/connectors/monitoring/MonitoringEnableDialog.vue';
import MonitoringDisableDialog from '@/components/core/connectors/monitoring/MonitoringDisableDialog.vue';
import BridgeMonitoring from '@/components/core/connectors/monitoring/BridgeMonitoring.vue';
import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { getColumnSize } from '@/utils/bridgeUtils';
import { isConnectorMonitoringSupported } from '@/utils/connectorMonitoring';
import { set } from 'lodash';
export default {
  name: 'ViewConnectorsCreate',
  components: {
    MonitoringEnableDialog,
    MonitoringDisableDialog,
    CoreConnectorsBridgeMonitoring: BridgeMonitoring
  },
  data() {
    return {
      cmOptions: {
        tabSize: 4,
        mode: {
          name: 'javascript',
          json: true
        },
        firstLineNumber: 0,
        theme: this.$store.state.app.UiTheme ? 'base16-dark' : 'duotone-light',
        lineNumbers: false,
        line: true,
        foldGutter: true,
        styleActiveLine: true,
        styleSelectedText: true,
        hintOptions: {
          completeSingle: false
        },
        lineWrapping: false
      },
      connectorId: parseInt((this.$route.params || {}).id) || null,
      selectedAccount: {},
      accounts: [],
      tabItem: 'tab-metadata',
      connector: {},
      bridgeValidation: {},
      bridgeTypes: [],
      lastSavedBridge: {},
      // Monitoring
      monitoringConfig: null,
      // set only by the dialog confirm handlers, cleared on save — dialog cancel must never touch it
      monitoringPendingAction: null,
      // null | 'enable-reuse' | 'enable-new' | 'pause' | 'delete'
      monitoringEnableDialog: false,
      monitoringDisableDialog: false,
      monitoringDisableMode: 'pause',
      monitoringUnsupportedByPolicy: false
    };
  },
  computed: {
    ...mapState('app', ['color', 'inpOutlined', 'inpFilled']),
    ...mapState('authentication', ['tenantId']),
    bridgeTypeMap() {
      const map = {};
      this.bridgeTypes.forEach(type => {
        map[type.type] = type;
      });
      return map;
    },
    headerBG() {
      return './img/dtwin-factory1.png';
    },
    canSave() {
      return this.connector.type !== null && this.connector.name && (this.selectedAccount || {}).id;
    },
    hasConnectionDataError() {
      return get(this.bridgeValidation, 'data.length', 0) > 0;
    },
    apiUrl() {
      return `${location.origin}/webhook?token=${this.connector.bridge.config.token}`;
    },
    apiUrlChirpstack() {
      return `${location.origin}/chirpstack?token=${this.connector.bridge.config.token}`;
    },
    apiUrlLwM2M() {
      return `${location.origin}/lwm2m?token=${this.connector.bridge.config.token}`;
    },
    isEditMode() {
      return this.connector.id !== undefined && this.connector.id !== null;
    },
    currentBridgeManifest() {
      const connectorType = get(this.connector, 'type');
      if (!connectorType) {
        return null;
      }
      return get(this.bridgeTypeMap, `${connectorType}.manifest`) || null;
    },
    isMonitoringSupportedByManifest() {
      return isConnectorMonitoringSupported(this.currentBridgeManifest);
    },
    showMonitoringControls() {
      var _this$connector;
      return Boolean(((_this$connector = this.connector) === null || _this$connector === void 0 ? void 0 : _this$connector.id) && this.isMonitoringSupportedByManifest && !this.monitoringUnsupportedByPolicy);
    },
    connectorStateBadge() {
      var _this$connector2, _this$connector3;
      const state = ((_this$connector2 = this.connector) === null || _this$connector2 === void 0 ? void 0 : _this$connector2.id) && ((_this$connector3 = this.connector) === null || _this$connector3 === void 0 ? void 0 : _this$connector3.state);
      if (!state) {
        return null;
      }
      const colors = {
        active: 'success',
        error: 'danger',
        waiting: 'info',
        assigning: 'warning',
        unassigning: 'warning',
        disabled: 'grey',
        unknown: 'grey'
      };
      const badgeKey = this.$i18n_te(`Connectors.Connectors.stateBadge.${state}`) ? `Connectors.Connectors.stateBadge.${state}` : 'Connectors.Connectors.stateBadge.unknown';
      return {
        color: colors[state] || 'grey',
        text: this.$i18n_t(badgeKey)
      };
    },
    pendingChangeBanners() {
      var _this$connector4, _this$lastSavedBridge;
      const banners = [];
      const hasSavedState = ((_this$connector4 = this.connector) === null || _this$connector4 === void 0 ? void 0 : _this$connector4.id) && ((_this$lastSavedBridge = this.lastSavedBridge) === null || _this$lastSavedBridge === void 0 ? void 0 : _this$lastSavedBridge.id) === this.connector.id;
      if (hasSavedState && Boolean(this.connector.active) !== Boolean(this.lastSavedBridge.active)) {
        banners.push(this.connector.active ? this.$i18n_t('Connectors.Connectors.pendingEnabled') : this.$i18n_t('Connectors.Connectors.pendingDisabled'));
      }
      if (this.monitoringPendingAction) {
        banners.push(this.$i18n_t('Connectors.Monitoring.pendingChanges'));
      }
      return banners;
    }
  },
  watch: {
    '$route.params.id': {
      handler(id) {
        this.connectorId = parseInt(id) || null;
        this.handleConnectorIdChange();
      },
      deep: true,
      immediate: false
    },
    showMonitoringControls(isVisible) {
      if (!isVisible && this.tabItem === 'tab-monitoring') {
        this.tabItem = 'tab-metadata';
      }
    }
  },
  mounted() {
    this.fetchAccounts();
    this.loadData().then(() => {
      if (this.connectorId) {
        this.handleConnectorIdChange();
        this.fetchMonitoringConfig();

        // If query `tab` is `statuslogs` activate the tab and remove the query
        if (this.$route.query.tab === 'statuslogs') {
          this.tabItem = 'tab-statuslogs';
          this.$router.replace({
            ...this.$router.currentRoute,
            query: {
              tab: undefined
            }
          });
        }
      } else {
        this.setupNewConnector();
      }
    }).catch(error => {
      console.error(error);
      this.$snack('Error loading data', 'error');
    });
  },
  methods: {
    onMutatorScriptChange(newScript) {
      const clonedConnector = cloneDeep(this.connector);
      set(clonedConnector, 'bridge.mutator', newScript);
      this.connector = clonedConnector;
    },
    get,
    getColumnSize,
    handleTabChange(tab) {
      if (tab === 'tab-connectorlogs') {
        if (this.$refs.packetLogs) {
          this.$refs.packetLogs.fetchConnectorLogs();
        }
      } else if (tab === 'tab-statuslogs') {
        if (this.$refs.statusLogs) {
          this.$refs.statusLogs.fetchConnectorLogs();
        }
      }
    },
    handleConnectorIdChange() {
      // check if connector with id exists
      // - if not, go back to connectors list
      // - if yes, set connector data
      const connector = cloneDeep(this.bridges.find(bridge => bridge.id === this.connectorId));
      if (!connector) {
        this.$router.replace('/connectors/list');
        return;
      }
      this.connector = connector;
      this.lastSavedBridge = cloneDeep(connector);
      this.selectedAccount = this.accounts.find(account => account.id === this.connector.tenantId);
      this.monitoringUnsupportedByPolicy = false;
    },
    setupNewConnector() {
      this.bridgeValidations = {};
      this.connector = {
        name: this.$i18n_t('accMgmt.tenant.bridges.newBridge'),
        type: null,
        bridge: {
          config: {}
        },
        config: {
          parser: {}
        }
      };
      this.monitoringUnsupportedByPolicy = false;
    },
    fetchAccounts() {
      this.$store.dispatch('app/loadAccounts').then(accounts => {
        this.accounts = accounts;
      }).catch(error => {
        console.error(error);
        this.$snack(this.$i18n_t('NiotaClassic.Devices.snacks.errorFindingAccounts'), 'error');
      });
    },
    loadData() {
      this.isLoadingConnectors = true;
      this.bridgeTypes = [];
      this.bridges = [];
      return Promise.all([this.loadBridgeTypes(), this.loadBridgeConfigs()]).then(([types, configs]) => {
        this.bridgeTypes = types.map(type => {
          return {
            ...type,
            name: get(type, 'manifest.displayName')
          };
        }).sort((b1, b2) => {
          if (b1.name > b2.name) {
            return 1;
          }
          if (b1.name < b2.name) {
            return -1;
          }
          return 0;
        });
        this.bridges = configs.map(config => {
          const bridgeType = this.bridgeTypeMap[config.type];
          config.displayName = bridgeType ? get(bridgeType, 'manifest.displayName') : '';
          return config;
        });
        if (this.$route.query.showBridges !== undefined) {
          this.scrollBridgesIntoView();
        }
      }).catch(error => {
        console.error(error);
        this.$snack(this.$i18n_t('Common.errorLoadingData'), 'error');
      }).finally(() => {
        this.isLoadingConnectors = false;
      });
    },
    loadBridgeTypes() {
      return ApiService.getBridgeTypes();
    },
    loadBridgeConfigs() {
      const tenantId = this.$route.params.account || this.tenantId;
      if (this.connectorId) {
        return ApiService.getBridgesForTenantsPaginated([tenantId], 1, 10, true, this.connectorId.toString(), 'id', false).then(response => {
          const configs = response.data || [];
          if (configs.length > 0) {
            return configs;
          } else {
            console.warn(`Connector ${this.connectorId} not found in tenant ${tenantId}`);
            return [];
          }
        }).catch(error => {
          console.error('Error loading bridge config:', error);
          return [];
        });
      }
      return ApiService.getBridgesForTenantsPaginated([tenantId], 1, 50, true).then(response => {
        return response.data || [];
      }).catch(error => {
        console.error('Error loading bridge configs:', error);
        return [];
      });
    },
    bridgeConfigIsInbound(bridge) {
      return get(bridge, 'manifest.functionScope.invokeDirection.inbound', false);
    },
    bridgeConfigIsOutbound(bridge) {
      return get(bridge, 'manifest.functionScope.invokeDirection.outbound', false);
    },
    bridgeConfig(bridge) {
      return this.bridgeTypeMap[bridge.type];
    },
    bridgeConfigKeys(instance) {
      const bridgeManifest = this.bridgeConfig(instance).manifest;
      let keys = Object.keys(bridgeManifest.config[0]).filter(key => {
        if (get(bridgeManifest, `config[0][${key}].hidden`)) {
          return false;
        }
        // if field is readonly, only show if it has a value
        if (get(bridgeManifest, `config[0][${key}].readonly`)) {
          return !!instance.bridge.config[key];
        }
        const visibleConfig = get(bridgeManifest, `config[0][${key}].visible`) || {};
        const notVisibleConfig = get(bridgeManifest, `config[0][${key}].not_visible`) || {};
        if (!Object.keys(notVisibleConfig).every(dependentKey => {
          return instance.bridge.config[dependentKey] !== notVisibleConfig[dependentKey];
        })) {
          return false;
        }
        return Object.keys(visibleConfig).every(dependentKey => {
          return instance.bridge.config[dependentKey] === visibleConfig[dependentKey];
        });
      });
      return keys.sort((a, b) => {
        const orderIdxA = get(bridgeManifest, `config[0][${a}].orderIdx`) || 0;
        const orderIdxB = get(bridgeManifest, `config[0][${b}].orderIdx`) || 0;
        if (orderIdxA < orderIdxB) {
          return -1;
        }
        if (orderIdxA > orderIdxB) {
          return 1;
        }
        return a.localeCompare(b);
      });
    },
    bridgeHasValidParameters(strict = false) {
      let currentParameters = get(this.connector, 'bridge.config');
      let savedParameters = get(this.lastSavedBridge, 'bridge.config');
      return !get(this.bridgeValidation, 'error', strict ? true : !isEqual(currentParameters, savedParameters));
    },
    changedBridgeParams() {
      this.bridgeValidation = {};
    },
    shouldShowTemplateSelect(bridgeInstance) {
      const bridgeConfig = this.bridgeConfig(bridgeInstance);
      const templates = bridgeConfig.manifest.templates || [];
      return bridgeConfig.manifest.functionScope.invokeDirection.inbound && (templates.length > 1 || bridgeConfig.manifest.functionScope.editableMutator);
    },
    editConfigMutator(bridge) {
      if (bridge.bridge.mutator) {
        delete bridge.bridge.mutator;
        this.bridgeValidation = cloneDeep(this.bridgeValidation);
        return;
      }
      const template = this.bridgeConfig(bridge).manifest.templates.find(template => {
        return template.name === bridge.config.parser.script;
      });
      bridge.bridge.mutator = get(template, 'mutator.script');
      bridge.bridge = cloneDeep(bridge.bridge);
      this.bridgeValidation = cloneDeep(this.bridgeValidation);
    },
    validateConnector() {
      const scriptToEval = this.connector.bridge.mutator;
      const promiseValidate = ApiService.validateBridge(this.connector.type, this.connector.id, {
        ...this.connector.bridge.config,
        name: this.connector.name,
        description: this.connector.description
      });
      const promiseEvalMutator = scriptToEval ? LambdaApiService.evaluateScript({
        script: scriptToEval,
        scriptParams: [{}]
      }) : Promise.resolve({});
      Promise.all([promiseValidate, promiseEvalMutator]).then(([validationResult, mutatorResult]) => {
        const bridgeInstance = this.connector;
        const bridgeManifest = this.bridgeConfig(bridgeInstance).manifest;
        if (get(bridgeManifest, 'functionScope.validator.returnsConfig')) {
          // (parts of) the config is returned/calculated for this bridge-validator,
          // so overwrite/merge with the existing config
          bridgeInstance.bridge.config = {
            ...bridgeInstance.bridge.config,
            ...validationResult.data
          };
        }
        let errorMessage = get(validationResult, 'errorMessage', get(mutatorResult, 'error'));
        this.bridgeValidation = validationResult;
        this.connector.hasError = true;
        if (mutatorResult.syntaxError) {
          this.bridgeValidation = {
            ...mutatorResult,
            mutatorScriptError: true
          };
        }
        if (errorMessage) {
          this.$snack(errorMessage, 'error');
        } else {
          this.persistBridge();
        }
      }).catch(error => {
        console.error(error);
      });
    },
    async persistBridge() {
      let persistPromise = Promise.resolve();
      if (this.connector.id) {
        // update handled in validation step
        if (this.connector.bridge.config.service === 'Outlook365(OAuth2.0)') {
          this.$snack(this.$i18n_t('Connectors.Connectors.saveSuccess'), 'success');
          return;
        }
        persistPromise = ApiService.updateBridge(this.connector.id, this.connector);
      } else {
        persistPromise = ApiService.createBridge(this.selectedAccount.id, this.connector.type, this.connector);
      }
      try {
        var _this$monitoringConfi;
        const persistedBridge = await persistPromise;
        this.connector = persistedBridge;
        this.lastSavedBridge = persistedBridge;

        // save monitoring config after bridge is saved
        await this.saveMonitoringConfig();
        this.$snack(this.$i18n_t('Connectors.Connectors.saveSuccess'), 'success');

        // switch to monitoring tab if monitoring was enabled
        if ((_this$monitoringConfi = this.monitoringConfig) !== null && _this$monitoringConfi !== void 0 && _this$monitoringConfi.enabled) {
          this.tabItem = 'tab-monitoring';
        }
      } catch (error) {
        if (error.response && this.$i18n_t(`Error.${error.response.data.message}`)) {
          this.$snack(this.$i18n_t(`Error.${error.response.data.message}`), 'error');
        } else {
          this.$snack(`${error.response.data.message}`, 'error');
        }
      }
    },
    resetBridgeState() {
      ApiService.resetBridgeState(this.connector.id, 'waiting').then(persistedBridge => {
        this.connector = persistedBridge;
      }).catch(error => {
        console.error(error);
        this.$snack(error, 'error');
      });
    },
    // Monitoring Methods
    async fetchMonitoringConfig() {
      if (!this.connector.id) {
        return;
      }
      try {
        this.monitoringConfig = await ApiService.getBridgeMonitoringConfig(this.connector.id);
      } catch (error) {
        // No config exists - this is normal for new bridges
        this.monitoringConfig = null;
      }
    },
    openMonitoringPauseDialog() {
      this.monitoringDisableMode = 'pause';
      this.monitoringDisableDialog = true;
    },
    openMonitoringStopDeleteDialog() {
      this.monitoringDisableMode = 'delete';
      this.monitoringDisableDialog = true;
    },
    handleMonitoringEnableConfirm(reuseExisting) {
      this.monitoringPendingAction = reuseExisting ? 'enable-reuse' : 'enable-new';
      this.monitoringEnableDialog = false;
    },
    handleMonitoringDisableConfirm(deleteConfig) {
      this.monitoringPendingAction = deleteConfig ? 'delete' : 'pause';
      this.monitoringDisableDialog = false;
    },
    resetMonitoringPendingState() {
      this.monitoringPendingAction = null;
    },
    async enableMonitoringConfig(reuseExisting) {
      // If "Create New" was chosen and old config exists, delete it first.
      if (!reuseExisting && this.monitoringConfig) {
        await ApiService.deleteBridgeMonitoring(this.connector.id);
        this.monitoringConfig = null;
      }
      const data = {
        name: `Monitoring for ${this.connector.name}`,
        reuseExisting
      };
      const config = await ApiService.enableBridgeMonitoring(this.connector.id, data);
      this.monitoringConfig = config;
    },
    async disableMonitoringConfig(deleteConfig) {
      if (!this.monitoringConfig) {
        return;
      }
      if (deleteConfig) {
        await ApiService.deleteBridgeMonitoring(this.connector.id);
        this.monitoringConfig = null;
        return;
      }
      const config = await ApiService.disableBridgeMonitoring(this.connector.id);
      this.monitoringConfig = config;
    },
    handleUnsupportedMonitoringConnector() {
      this.monitoringUnsupportedByPolicy = true;
      this.resetMonitoringPendingState();
      this.monitoringEnableDialog = false;
      this.monitoringDisableDialog = false;
      this.tabItem = 'tab-metadata';
      this.$snack(this.$i18n_t('Error.MONITORING_UNSUPPORTED_CONNECTOR'), 'error');
    },
    isUnsupportedMonitoringError(error) {
      const rejectionCode = get(error, 'response.data.code') || get(error, 'response.data.type') || get(error, 'response.data.message');
      return rejectionCode === 'MONITORING_UNSUPPORTED_CONNECTOR';
    },
    async saveMonitoringConfig() {
      if (!this.connector.id) {
        console.warn('[Monitoring] Cannot save - connector has no ID yet');
        return;
      }
      if (!this.monitoringPendingAction) {
        console.warn('[Monitoring] No pending changes');
        return;
      }
      try {
        if (this.monitoringPendingAction === 'enable-reuse' || this.monitoringPendingAction === 'enable-new') {
          await this.enableMonitoringConfig(this.monitoringPendingAction === 'enable-reuse');
        } else {
          await this.disableMonitoringConfig(this.monitoringPendingAction === 'delete');
        }
        this.resetMonitoringPendingState();
      } catch (error) {
        if (this.isUnsupportedMonitoringError(error)) {
          this.handleUnsupportedMonitoringConnector();
          return;
        }
        console.error('[Monitoring] Error saving:', error);
        throw error;
      }
    }
  }
};